// SETTINGS V2 — Location create/edit dialog (SETTINGS_V2_SP4 §8).
//
// ONE dialog for create + edit (mirrors ContactDialogV2). Consumes
// SettingsV2DialogShell (620px desktop / fullscreen phone). Open-state in
// settingsV2UiSlice.openDialog === "location"; edit target + save/close/error
// ride down as props from LocationsPageV2 (page owns the fulfilled-gated dispatch
// + refetch/invalidate — §6).
//
// Payload (§8.5, AMENDED by MAP-A-FE1): description + addressLine2 keys retained
// (undefined, no UI control); no id/countryName/isDefault. 5-country stub (ids
// 1-5) preserved, labels i18n'd (§2/§9). Payload now ALSO carries lat/lng — armed
// by the AddressAutocomplete pick (the ONE sanctioned MUI-wrapping shared
// component, consumed as-is per SHARED_DEPLOY_AND_RULES decision 1). Manual-only
// entry sends neither (undefined): create → NULL, edit → BE1 preserves stored.
//
// FLAGGED (§8.3): NO Description control (server VMs lack it; value was discarded).
// Styling: inline + scoped CSS-var tokens + v2 atoms; no MUI/dt/Tailwind. SEC-REQ-2/3.
import React, { useEffect, useState } from "react";

import { useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import type { FormSelectOption } from "../../../../shared/components/redesign/forms/FormSelect";
import type {
  Location,
  LocationFormData,
} from "../../../machinery/components/locations/locationTypes";
import { CountryCode } from "../../../../shared/models/countryModels";
import AddressAutocomplete, {
  AddressData,
} from "../../../../shared/components/AddressAutocomplete";
import { SField, SInputField, SSelectField, SErrorCallout } from "../atoms";
import SettingsV2DialogShell from "./SettingsV2DialogShell";

// Frozen 5-country stub (§0.10) ↔ ISO-3166 numeric CountryCode. Local (5 entries,
// no premature shared helper): restrict the search + map a picked country back.
const COUNTRY_CODE_TO_STUB_ID: Partial<Record<CountryCode, number>> = {
  [CountryCode.CZ]: 1, [CountryCode.SK]: 2, [CountryCode.DE]: 3,
  [CountryCode.AT]: 4, [CountryCode.PL]: 5,
};
const STUB_ID_TO_COUNTRY_CODE: Record<number, CountryCode> = {
  1: CountryCode.CZ, 2: CountryCode.SK, 3: CountryCode.DE,
  4: CountryCode.AT, 5: CountryCode.PL,
};
const stubIdToCountryCode = (id: number | ""): CountryCode | undefined =>
  id === "" ? undefined : STUB_ID_TO_COUNTRY_CODE[id];

export interface LocationDialogV2Props {
  editLocation: Location | null;
  parentOptions: FormSelectOption[];
  onSave: (data: LocationFormData) => void;
  onClose: () => void;
  submitting: boolean;
  dialogError: string | null;
}

const LocationDialogV2: React.FC<LocationDialogV2Props> = ({
  editLocation,
  parentOptions,
  onSave,
  onClose,
  submitting,
  dialogError,
}) => {
  const { translate: t } = useTranslation("settings");
  const { translate: tCommon } = useTranslation("common");
  const { isPhone } = useBreakpoint();

  const open = useAppSelector((s) => s.settingsV2Ui.openDialog === "location");

  const [name, setName] = useState("");
  const [parentLocationId, setParentLocationId] = useState<string>("");
  const [addressLine1, setAddressLine1] = useState("");
  const [city, setCity] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [countryId, setCountryId] = useState<number | "">("");
  const [nameError, setNameError] = useState(false);
  // Coords armed by an autocomplete pick. null = manual/no pick (payload omits
  // lat/lng). Reset on every open — the field always starts blank (§0.7).
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);

  // Seed on open (§8.2): create = empty; edit = seed from editLocation. Address
  // fields are always visible (SP4.3 — no expand state to seed).
  useEffect(() => {
    if (!open) return;
    if (editLocation) {
      setName(editLocation.name ?? "");
      setParentLocationId(editLocation.parentLocationId ?? "");
      setAddressLine1(editLocation.addressLine1 ?? "");
      setCity(editLocation.city ?? "");
      setZipCode(editLocation.zipCode ?? "");
      setCountryId(editLocation.countryId ?? "");
    } else {
      setName("");
      setParentLocationId("");
      setAddressLine1("");
      setCity("");
      setZipCode("");
      setCountryId("");
    }
    setCoords(null);
    setNameError(false);
  }, [open, editLocation]);

  // Pick → fill address/city/zip (non-empty only), map country to the 5-stub
  // (unmapped → leave countryId), arm coords when both present. RAW setters (not
  // the manual wrappers) so the pick doesn't clear the coords it just set (§4.2).
  const handleAddressPick = (data?: AddressData) => {
    if (!data) return;
    if (data.address) setAddressLine1(data.address);
    if (data.city) setCity(data.city);
    if (data.zipCode) setZipCode(data.zipCode);
    if (data.countryCode != null) {
      const stubId = COUNTRY_CODE_TO_STUB_ID[data.countryCode];
      if (stubId != null) setCountryId(stubId);
    }
    if (data.latitude != null && data.longitude != null) {
      setCoords({ lat: data.latitude, lng: data.longitude });
    }
  };

  // Manual-edit invalidation (§4.2): a manual address/city/ZIP change means the
  // pick no longer describes the text → drop coords. Country-select does NOT.
  const handleManualAddress = (v: string) => { setAddressLine1(v); setCoords(null); };
  const handleManualCity = (v: string) => { setCity(v); setCoords(null); };
  const handleManualZip = (v: string) => { setZipCode(v); setCoords(null); };

  // 5-country stub (§2 — ids/count preserved 1-5; labels i18n'd, §9).
  const countryOptions: FormSelectOption[] = [
    { value: 1, label: t("settings:locations.countries.czechia") },
    { value: 2, label: t("settings:locations.countries.slovakia") },
    { value: 3, label: t("settings:locations.countries.germany") },
    { value: 4, label: t("settings:locations.countries.austria") },
    { value: 5, label: t("settings:locations.countries.poland") },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setNameError(true);
      return;
    }
    // Payload (§8.5, amended by MAP-A-FE1) — lat/lng ride from the armed pick
    // (undefined when manual-only); description/addressLine2 retained undefined.
    onSave({
      name: name.trim(),
      parentLocationId: parentLocationId || undefined,
      description: undefined,
      addressLine1: addressLine1.trim() || undefined,
      addressLine2: undefined,
      city: city.trim() || undefined,
      zipCode: zipCode.trim() || undefined,
      countryId: countryId ? Number(countryId) : undefined,
      latitude: coords?.lat,
      longitude: coords?.lng,
    });
  };

  const nameErrorMsg = nameError ? tCommon("forms.required") : undefined;

  const footer = (
    <>
      <button type="button" className="btn btn-ghost" onClick={onClose}>
        {tCommon("buttons.cancel")}
      </button>
      <button
        type="submit"
        form="locationFormV2"
        className="btn btn-primary"
        disabled={submitting}
        style={{ background: "var(--mod-accent)", borderColor: "var(--mod-accent)" }}
      >
        {editLocation
          ? tCommon("saveChanges")
          : `＋ ${t("settings:locations.addLocation")}`}
      </button>
    </>
  );

  const col2: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: isPhone ? "1fr" : "1fr 1fr",
    gap: 12,
  };

  return (
    <SettingsV2DialogShell
      open={open}
      onClose={onClose}
      width={620}
      title={
        editLocation
          ? t("settings:locations.editTitle")
          : t("settings:locations.addLocation")
      }
      labelledById="sv2-location-dialog-title"
      busy={submitting}
      footerNote={t("settings:locations.dialogFooterNote")}
      footer={footer}
    >
      <form
        id="locationFormV2"
        onSubmit={handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
      >
        <SInputField
          label={t("settings:locations.nameLabel")}
          required
          maxLength={200}
          value={name}
          onChange={(v) => {
            setName(v);
            if (v.trim()) setNameError(false);
          }}
          placeholder={t("settings:locations.namePlaceholder")}
          error={nameErrorMsg}
        />

        <SSelectField
          label={t("settings:locations.parentLabel")}
          value={parentLocationId}
          onChange={(v) => setParentLocationId(v === "" ? "" : String(v))}
          options={parentOptions}
          placeholder={t("settings:locations.parentNone")}
        />

        {/* Static "Address details" section divider — NON-interactive. The address
            fields below are visible by default in both Add and Edit (user feedback
            SP4.3): the previous collapse-to-reveal button + chevron were removed so
            users never have to click to see the address inputs. */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            width: "100%",
            padding: "2px 0",
          }}
        >
          <span className="eyebrow" style={{ color: "var(--muted)" }}>
            {t("settings:locations.addressSection")}
          </span>
          <span style={{ flex: 1, height: 1, background: "var(--border-soft)" }} />
        </div>

        {/* MAP-A-FE1: Places search — fills manual fields + arms lat/lng (§4.2).
            The relative+zIndex wrapper lifts the gmp-place-autocomplete shadow-DOM
            predictions above the later position:relative MUI fields below, which
            otherwise paint over the dropdown ("no suggestions" root cause). */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ position: "relative", zIndex: 5 }}>
            <SField label={t("settings:locations.searchAddressLabel")}>
              <AddressAutocomplete
                countryCode={stubIdToCountryCode(countryId)}
                placeholder={t("settings:locations.searchAddressPlaceholder")}
                onChange={handleAddressPick}
              />
            </SField>
          </div>
          <SInputField
            label={t("settings:locations.addressLabel")}
            value={addressLine1}
            onChange={handleManualAddress}
            placeholder={t("settings:locations.addressPlaceholder")}
          />
          <div style={col2}>
            <SInputField
              label={t("settings:locations.cityLabel")}
              value={city}
              onChange={handleManualCity}
              placeholder={t("settings:locations.cityPlaceholder")}
            />
            <SInputField
              label={t("settings:locations.zipLabel")}
              mono
              value={zipCode}
              onChange={handleManualZip}
              placeholder={t("settings:locations.zipPlaceholder")}
            />
          </div>
          <SSelectField
            label={t("settings:locations.countryLabel")}
            value={countryId}
            onChange={(v) => setCountryId(v === "" ? "" : Number(v))}
            options={countryOptions}
            placeholder={t("settings:locations.countryPlaceholder")}
          />
        </div>

        {/* In-dialog error callout (§7 row 3 / §8.4) — dialog stays open, form preserved. */}
        {dialogError && (
          <SErrorCallout title={t("settings:locations.saveFailed")} body={dialogError} />
        )}
      </form>
    </SettingsV2DialogShell>
  );
};

export default LocationDialogV2;
